import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import {
  Building2,
  Users,
  Mail,
  Inbox,
  Activity,
  ArrowLeft,
  RefreshCw,
  AlertTriangle,
  Trash2,
  UserPlus,
  CheckCircle,
  XCircle,
  ChevronDown,
} from 'lucide-react';
import { systemApi } from '../../../api/client';
import type { SystemOrgDetail, OrgMember, OrgJoinRequest, OrgAuditEntry, OrgRole } from '../../../api/client';
import { RoleBadge } from '../../../components/team/RoleBadge';

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const VALID_ROLES: OrgRole[] = ['admin', 'member', 'viewer'];

// ── Members Tab ────────────────────────────────────────────────────────────────

function MembersTab({ orgId }: { orgId: string }) {
  const [members, setMembers]     = useState<OrgMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [roleDropdown, setRoleDropdown]   = useState<string | null>(null);

  const load = () => {
    setIsLoading(true);
    systemApi.listOrgMembers(orgId)
      .then(setMembers)
      .catch((err) => setError((err as Error).message))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => { load(); }, [orgId]);

  const handleRoleChange = async (userId: string, role: OrgRole) => {
    setActionLoading(userId);
    setRoleDropdown(null);
    try {
      await systemApi.updateOrgMemberRole(orgId, userId, role);
      setMembers((prev) => prev.map((m) => m.userId === userId ? { ...m, role } : m));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemove = async (userId: string) => {
    setActionLoading(userId);
    try {
      await systemApi.removeOrgMember(orgId, userId);
      setMembers((prev) => prev.filter((m) => m.userId !== userId));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setActionLoading(null);
    }
  };

  if (isLoading) return <LoadingRow text="Loading members…" />;

  return (
    <div className="space-y-4">
      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      <div className="card overflow-hidden">
        {members.length === 0 ? (
          <EmptyState icon={Users} text="No members found." />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-surface-4">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Member</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Role</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Joined</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.userId} className="border-b border-surface-4/50 last:border-0 hover:bg-surface-2/30">
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-medium text-gray-200 text-sm">{m.displayName ?? m.email}</p>
                      {m.displayName && <p className="text-xs text-gray-500">{m.email}</p>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="relative inline-block">
                      <button
                        onClick={() => setRoleDropdown(roleDropdown === m.userId ? null : m.userId)}
                        disabled={!!actionLoading}
                        className="flex items-center gap-1.5 text-xs hover:opacity-80 transition-opacity"
                      >
                        <RoleBadge role={m.role} size="sm" />
                        <ChevronDown className="w-3 h-3 text-gray-600" />
                      </button>
                      {roleDropdown === m.userId && (
                        <div className="absolute z-10 mt-1 bg-surface-2 border border-surface-4 rounded-md shadow-lg py-1 min-w-[120px]">
                          {VALID_ROLES.map((r) => (
                            <button
                              key={r}
                              onClick={() => handleRoleChange(m.userId, r)}
                              className={clsx(
                                'w-full text-left px-3 py-1.5 text-xs hover:bg-surface-3 transition-colors',
                                m.role === r ? 'text-brand' : 'text-gray-300',
                              )}
                            >
                              {r}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500 hidden md:table-cell">{formatDate(m.joinedAt)}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => handleRemove(m.userId)}
                      disabled={actionLoading === m.userId}
                      className="p-1.5 text-gray-600 hover:text-loss hover:bg-loss/10 rounded transition-colors disabled:opacity-50"
                      title="Remove member"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── Invitations Tab ────────────────────────────────────────────────────────────

function InvitationsTab({ orgId }: { orgId: string }) {
  const [email, setEmail]         = useState('');
  const [role, setRole]           = useState<OrgRole>('member');
  const [isSending, setIsSending] = useState(false);
  const [success, setSuccess]     = useState<string | null>(null);
  const [error, setError]         = useState<string | null>(null);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setIsSending(true);
    setError(null);
    try {
      await systemApi.sendOrgInvitation(orgId, email.trim(), role);
      setSuccess(`Invitation sent to ${email.trim()}`);
      setEmail('');
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="space-y-4">
      {error   && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      {success && <SuccessBanner message={success} />}
      <div className="op-panel p-5">
        <h3 className="text-sm font-semibold text-gray-200 mb-4">Send Invitation</h3>
        <form onSubmit={handleSend} className="flex flex-wrap gap-3">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email address"
            className="flex-1 min-w-[200px] px-3 py-2 bg-surface-2 border border-surface-4 rounded-md text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:border-brand/60"
          />
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as OrgRole)}
            className="px-3 py-2 bg-surface-2 border border-surface-4 rounded-md text-sm text-gray-300 focus:outline-none focus:border-brand/60"
          >
            {VALID_ROLES.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
          <button
            type="submit"
            disabled={isSending || !email.trim()}
            className="flex items-center gap-2 px-4 py-2 bg-brand/20 hover:bg-brand/30 text-brand border border-brand/30 rounded-md text-sm font-medium transition-colors disabled:opacity-50"
          >
            <UserPlus className="w-4 h-4" />
            {isSending ? 'Sending…' : 'Send Invite'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Join Requests Tab ──────────────────────────────────────────────────────────

function JoinRequestsTab({ orgId }: { orgId: string }) {
  const [requests, setRequests]   = useState<OrgJoinRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const load = () => {
    setIsLoading(true);
    systemApi.listOrgJoinRequests(orgId)
      .then(setRequests)
      .catch((err) => setError((err as Error).message))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => { load(); }, [orgId]);

  const handleAction = async (id: string, action: 'accept' | 'deny') => {
    setActionLoading(id);
    try {
      if (action === 'accept') {
        await systemApi.acceptOrgJoinRequest(orgId, id);
      } else {
        await systemApi.denyOrgJoinRequest(orgId, id);
      }
      setRequests((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setActionLoading(null);
    }
  };

  if (isLoading) return <LoadingRow text="Loading join requests…" />;

  return (
    <div className="space-y-4">
      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      <div className="card overflow-hidden">
        {requests.length === 0 ? (
          <EmptyState icon={Inbox} text="No pending join requests." />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-surface-4">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">User</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Message</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Requested</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {requests.map((r) => (
                <tr key={r.id} className="border-b border-surface-4/50 last:border-0 hover:bg-surface-2/30">
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-200 text-sm">{r.userDisplayName ?? r.userEmail}</p>
                    {r.userDisplayName && <p className="text-xs text-gray-500">{r.userEmail}</p>}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500 hidden md:table-cell max-w-[240px] truncate">{r.message ?? '—'}</td>
                  <td className="px-4 py-3 text-xs text-gray-500 hidden md:table-cell">{formatDate(r.createdAt)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 justify-end">
                      <button
                        onClick={() => handleAction(r.id, 'accept')}
                        disabled={actionLoading === r.id}
                        className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-win border border-win/30 bg-win/10 hover:bg-win/20 rounded transition-colors disabled:opacity-50"
                      >
                        <CheckCircle className="w-3 h-3" /> Accept
                      </button>
                      <button
                        onClick={() => handleAction(r.id, 'deny')}
                        disabled={actionLoading === r.id}
                        className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-loss border border-loss/30 bg-loss/10 hover:bg-loss/20 rounded transition-colors disabled:opacity-50"
                      >
                        <XCircle className="w-3 h-3" /> Deny
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── Settings Tab ───────────────────────────────────────────────────────────────

function SettingsTab({ orgId, orgName }: { orgId: string; orgName: string }) {
  const [name, setName]           = useState(orgName);
  const [isSaving, setIsSaving]   = useState(false);
  const [success, setSuccess]     = useState<string | null>(null);
  const [error, setError]         = useState<string | null>(null);

  useEffect(() => { setName(orgName); }, [orgName]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setIsSaving(true);
    setError(null);
    try {
      await systemApi.updateOrganization(orgId, { name: name.trim() });
      setSuccess('Organization name updated.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-4 max-w-lg">
      {error   && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      {success && <SuccessBanner message={success} />}
      <div className="op-panel p-5">
        <h3 className="text-sm font-semibold text-gray-200 mb-4">Organization Name</h3>
        <form onSubmit={handleSave} className="flex gap-3">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="flex-1 px-3 py-2 bg-surface-2 border border-surface-4 rounded-md text-sm text-gray-100 focus:outline-none focus:border-brand/60"
          />
          <button
            type="submit"
            disabled={isSaving || !name.trim() || name.trim() === orgName}
            className="px-4 py-2 bg-brand/20 hover:bg-brand/30 text-brand border border-brand/30 rounded-md text-sm font-medium transition-colors disabled:opacity-50"
          >
            {isSaving ? 'Saving…' : 'Save'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Audit Tab ──────────────────────────────────────────────────────────────────

function AuditTab({ orgId }: { orgId: string }) {
  const [entries, setEntries]     = useState<OrgAuditEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError]         = useState<string | null>(null);

  useEffect(() => {
    setIsLoading(true);
    systemApi.getOrgAuditLog(orgId, { limit: 50 })
      .then(setEntries)
      .catch((err) => setError((err as Error).message))
      .finally(() => setIsLoading(false));
  }, [orgId]);

  if (isLoading) return <LoadingRow text="Loading audit log…" />;

  return (
    <div className="space-y-4">
      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      <div className="card overflow-hidden">
        {entries.length === 0 ? (
          <EmptyState icon={Activity} text="No audit log entries." />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-surface-4">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Action</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">Actor</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Date</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} className="border-b border-surface-4/50 last:border-0 hover:bg-surface-2/30">
                  <td className="px-4 py-3 font-mono text-xs text-brand-light">{e.action}</td>
                  <td className="px-4 py-3 text-xs text-gray-400 hidden sm:table-cell">{e.actorEmail ?? e.actorId}</td>
                  <td className="px-4 py-3 text-xs text-gray-500 hidden md:table-cell">{formatDate(e.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── Shared micro-components ────────────────────────────────────────────────────

function LoadingRow({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-2 text-sm text-gray-500 py-8">
      <RefreshCw className="w-4 h-4 animate-spin" />
      {text}
    </div>
  );
}

function EmptyState({ icon: Icon, text }: { icon: React.ElementType; text: string }) {
  return (
    <div className="flex flex-col items-center py-12 text-gray-500 gap-2">
      <Icon className="w-8 h-8 text-gray-700" />
      <p className="text-sm">{text}</p>
    </div>
  );
}

function ErrorBanner({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  return (
    <div className="flex items-center gap-3 p-3 bg-loss/10 border border-loss/20 rounded-lg">
      <AlertTriangle className="w-4 h-4 text-loss flex-shrink-0" />
      <p className="text-sm text-loss">{message}</p>
      <button onClick={onDismiss} className="ml-auto text-loss/60 hover:text-loss" aria-label="Dismiss">×</button>
    </div>
  );
}

function SuccessBanner({ message }: { message: string }) {
  return (
    <div className="flex items-center gap-3 p-3 bg-win/10 border border-win/20 rounded-lg">
      <CheckCircle className="w-4 h-4 text-win flex-shrink-0" />
      <p className="text-sm text-win">{message}</p>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

type Tab = 'members' | 'invitations' | 'join-requests' | 'settings' | 'audit';

const TABS: { id: Tab; label: string; icon: React.ElementType }[] = [
  { id: 'members',      label: 'Members',       icon: Users },
  { id: 'invitations',  label: 'Invitations',   icon: Mail },
  { id: 'join-requests',label: 'Join Requests', icon: Inbox },
  { id: 'settings',     label: 'Settings',      icon: Building2 },
  { id: 'audit',        label: 'Audit Log',     icon: Activity },
];

export function SystemOrgDetailPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate  = useNavigate();
  const [org, setOrg]             = useState<SystemOrgDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('members');

  useEffect(() => {
    if (!orgId) return;
    setIsLoading(true);
    systemApi.getOrganization(orgId)
      .then(setOrg)
      .catch((err) => setError((err as Error).message))
      .finally(() => setIsLoading(false));
  }, [orgId]);

  if (!orgId) return null;

  return (
    <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/settings/system')}
          className="p-1.5 text-gray-500 hover:text-gray-300 hover:bg-surface-3 rounded-md transition-colors"
          aria-label="Back to organizations"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div>
          {isLoading ? (
            <div className="h-6 w-48 bg-surface-3 rounded animate-pulse" />
          ) : error ? (
            <p className="text-sm text-loss">{error}</p>
          ) : (
            <>
              <h2 className="text-lg font-semibold text-gray-100">{org?.name}</h2>
              <p className="text-xs text-gray-500 font-mono">{org?.slug} · {org?.memberCount} members</p>
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-surface-4">
        <nav className="flex gap-1 -mb-px" aria-label="Org detail tabs">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={clsx(
                'flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors',
                activeTab === id
                  ? 'border-brand text-brand'
                  : 'border-transparent text-gray-500 hover:text-gray-300 hover:border-surface-4',
              )}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === 'members'       && <MembersTab orgId={orgId} />}
      {activeTab === 'invitations'   && <InvitationsTab orgId={orgId} />}
      {activeTab === 'join-requests' && <JoinRequestsTab orgId={orgId} />}
      {activeTab === 'settings'      && <SettingsTab orgId={orgId} orgName={org?.name ?? ''} />}
      {activeTab === 'audit'         && <AuditTab orgId={orgId} />}
    </div>
  );
}
